/**
 * Review queue store — the single shared keep/skip queue (docs/design/
 * STEAL-SPEC.md "the architectural insight": one apply flow, two plan
 * generators). ShiftBar's /api/shift path and Steal from Masters'
 * planFromInfluence both produce a ShiftPlanStep[] and hand it to loadPlan;
 * ReviewQueue.tsx is the one rendered instance both feed.
 */
import { create } from 'zustand';
import type { ShiftPlanStep } from '@/services/shift-service';

export type StepStatus = 'pending' | 'kept' | 'skipped' | 'not-applicable';

export interface ReviewStep extends ShiftPlanStep {
  status: StepStatus;
}

interface ReviewQueueState {
  error: string | null;
  summary: string;
  steps: ReviewStep[] | null;

  loadPlan: (plan: ShiftPlanStep[], summary?: string) => void;
  loadError: (message: string) => void;
  setStepStatus: (index: number, status: StepStatus) => void;
  dismiss: () => void;
}

export const useReviewQueueStore = create<ReviewQueueState>((set) => ({
  error: null,
  summary: '',
  steps: null,

  loadPlan: (plan, summary = '') =>
    set({
      error: null,
      summary,
      steps: plan.map((step) => ({ ...step, status: 'pending' as StepStatus })),
    }),

  loadError: (message) => set({ steps: null, summary: '', error: message }),

  setStepStatus: (index, status) =>
    set((state) => ({
      steps: state.steps ? state.steps.map((s, i) => (i === index ? { ...s, status } : s)) : state.steps,
    })),

  dismiss: () => set({ steps: null, summary: '', error: null }),
}));
